import { AxiosError } from 'axios';

import { AUTH_TOKEN_KEY } from '@/constant/localStorage';
import api from '@/services/axiosInstance';
import {
  ChangePasswordData,
  CompanyRegistration,
  CreateCompanyRegistrationData,
  RegisterData,
  SetupCompanyBcIntegrationRequest,
  UpdateProfileData,
  User,
} from '@/types/auth';
import { isValidE164Format } from '@/utils/phoneFormatter';
import { clearAllQuoteDocumentRecovery } from '@/utils/quoteDocumentRecovery';
import storage from '@/utils/storage';

const AUTH_API_URL = 'https://api.vh3connect.io/api:G6skVfcT/auth';

const PROFILE_API_URL = 'https://api.vh3connect.io/api:DfR0osYB';

const CHANGE_PASSWORD_URL =
  'https://api.vh3connect.io/api:6hazS0TY/update_password';

const VERIFY_TOKEN_URL =
  'https://api.vh3connect.io/api:G6skVfcT/auth/token-verify';

const JOIN_COMPANY_URL = 'https://api.vh3connect.io/api:G6skVfcT/auth/join';

const REGISTER_URL = 'https://api.vh3connect.io/api:G6skVfcT/auth/signup';

const VERIFY_EMAIL_URL =
  'https://api.vh3connect.io/api:G6skVfcT/auth/email-token-verify';

const GOOGLE_OAUTH_INIT_URL =
  'https://api.vh3connect.io/api:U0aE1wpF/oauth/google/init';

const REGISTER_COMPANY_URL =
  'https://api.vh3connect.io/api:G6skVfcT/auth/company-register';

const SETUP_COMPANY_BC_INTEGRATION_URL =
  'https://api.vh3connect.io/api:G6skVfcT/auth/company-bc-integration_v2';

const GET_COMPANY_REGISTRATION_URL =
  'https://api.vh3connect.io/api:26z-xxPu/company_registration';

const FORGOT_PASSWORD_URL =
  'https://api.vh3connect.io/api:G6skVfcT/auth/forgot-password';

const RESET_PASSWORD_URL =
  'https://api.vh3connect.io/api:G6skVfcT/auth/reset-password';

class AuthError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'AuthError';
  }
}

const formatCredentials = (email: string, password: string) => {
  const formattedEmail = email.trim().toLowerCase();

  if (!formattedEmail) {
    throw new AuthError('Email is required');
  }

  if (!password) {
    throw new AuthError('Password is required');
  }

  if (!formattedEmail.includes('@')) {
    throw new AuthError('Invalid email format');
  }

  return {
    email: formattedEmail,
    password,
  };
};

const handleAuthError = (error: unknown): never => {
  if (error instanceof AuthError) {
    throw error;
  }

  if (error instanceof AxiosError) {
    const status = error.response?.status;
    const message = error.response?.data?.message;

    switch (status) {
      case 403:
        throw new AuthError(message || 'Invalid email or password');

      case 401:
        storage.removeItem(AUTH_TOKEN_KEY);
        throw new AuthError('Your session has expired. Please log in again.');

      case 404:
        throw new AuthError('Service not available. Please try again later.');

      case 429:
        throw new AuthError('Too many attempts. Please try again later.');

      case 500:
        throw new AuthError(
          'An unexpected error occurred. Please try again later.'
        );

      default:
        throw new AuthError(
          message || 'Authentication failed. Please try again.'
        );
    }
  }

  throw new AuthError('An unexpected error occurred. Please try again later.');
};

interface AuthResponse {
  authToken: string;
  user?: User;
}

function saveAuthToken(authToken: string) {
  if (!authToken) {
    throw new AuthError('Invalid response from server');
  }

  storage.setItem(AUTH_TOKEN_KEY, authToken);

  return auth.me();
}

/**
 * Finish any sign-in (password, passkey, later OAuth): store the token and load the User.
 * Every sign-in method ends here.
 */
export function completeLogin(authToken: string) {
  return saveAuthToken(authToken);
}

export const auth = {
  login: async (email: string, password: string): Promise<User | undefined> => {
    try {
      const credentials = formatCredentials(email, password);
      console.log('🔑 Logging in:', { email: credentials.email });

      const { data } = await api.post<AuthResponse>(
        `${AUTH_API_URL}/login`,
        credentials
      );

      return saveAuthToken(data.authToken);
    } catch (error) {
      handleAuthError(error);
    }
  },

  signup: async (
    firstName: string,
    lastName: string,
    email: string,
    password: string,
    phone: string
  ): Promise<User | undefined> => {
    try {
      const { email: formattedEmail } = formatCredentials(email, password);

      if (!firstName.trim()) {
        throw new AuthError('First name is required');
      }

      if (!lastName.trim()) {
        throw new AuthError('Last name is required');
      }

      if (!phone.trim()) {
        throw new AuthError('Phone number is required');
      }

      // Format phone number to match required format
      const formattedPhone = phone.trim().replace(/^0+/, ''); // Remove leading zeros
      // if (!/^44\d{10}$/.test(formattedPhone)) {
      if (!isValidE164Format(formattedPhone)) {
        throw new AuthError('Phone number must be in format: +44 456 678 8990');
      }

      console.log('📝 Signing up:', {
        email: formattedEmail,
        phone: formattedPhone,
        firstName,
        lastName,
      });

      const credentials = {
        username: `${firstName.trim()} ${lastName.trim()}`,
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        email: formattedEmail,
        password,
        phone: formattedPhone,
      };

      const { data } = await api.post<AuthResponse>(
        `${AUTH_API_URL}/signup`,
        credentials
      );

      return saveAuthToken(data.authToken);
    } catch (error) {
      handleAuthError(error);
    }
  },

  me: async (): Promise<User | undefined> => {
    try {
      const token = storage.getItem(AUTH_TOKEN_KEY);
      if (!token) {
        throw new AuthError('No authentication token found');
      }

      const { data } = await api.get<User>(`${AUTH_API_URL}/me`);
      return data;
    } catch (error) {
      handleAuthError(error);
    }
  },

  logout: () => {
    storage.removeItem(AUTH_TOKEN_KEY);
    // The AI disclaimer is accepted once per device; signing out keeps it accepted.
    clearAllQuoteDocumentRecovery();
  },

  isAuthenticated: (): boolean => {
    return !!storage.getItem(AUTH_TOKEN_KEY);
  },

  updateProfile: async (
    profileData: Partial<UpdateProfileData>
  ): Promise<Partial<User> | undefined> => {
    try {
      const { data } = await api.put<Partial<User>>(
        `${PROFILE_API_URL}/profile`,
        profileData
      );
      return data;
    } catch (error) {
      handleAuthError(error);
    }
  },

  changePassword: async ({
    currentPassword,
    newPassword,
    confirmNewPassword,
  }: ChangePasswordData): Promise<void> => {
    try {
      const { data } = await api.put<void>(`${CHANGE_PASSWORD_URL}`, {
        password: newPassword,
        confirm_password: confirmNewPassword,
        previousPassword: currentPassword,
      });

      return data;
    } catch (error) {
      handleAuthError(error);
    }
  },

  verifyToken: async (token: string): Promise<User | undefined> => {
    try {
      const { data } = await api.get<User>(
        `${VERIFY_TOKEN_URL}?token=${token}`
      );
      return data;
    } catch (error) {
      handleAuthError(error);
    }
  },

  joinCompany: async (params: {
    email: string;
    password: string;
    token: string;
    firstName?: string;
    lastName?: string;
    phone?: string;
  }): Promise<User | undefined> => {
    try {
      const { data } = await api.post<AuthResponse>(JOIN_COMPANY_URL, params);

      return saveAuthToken(data.authToken);
    } catch (error) {
      handleAuthError(error);
    }
  },

  register: async (params: RegisterData): Promise<void> => {
    try {
      const { data } = await api.post<void>(REGISTER_URL, {
        first_name: params.firstName,
        last_name: params.lastName,
        email: params.email,
        password: params.password,
      });

      return data;
    } catch (error) {
      handleAuthError(error);
    }
  },

  verifyEmail: async (token: string): Promise<User | undefined> => {
    try {
      const { data } = await api.post<AuthResponse>(VERIFY_EMAIL_URL, {
        token,
      });

      return saveAuthToken(data.authToken);
    } catch (error) {
      handleAuthError(error);
    }
  },

  getGoogleAuthUrl: async (redirectUrl: string) => {
    try {
      const { data } = await api.get<{ authUrl: string }>(
        `${GOOGLE_OAUTH_INIT_URL}?redirect_uri=${encodeURIComponent(redirectUrl)}`
      );
      return data.authUrl;
    } catch (error) {
      handleAuthError(error);
    }
  },

  createCompanyRegistration: async (
    params: CreateCompanyRegistrationData
  ): Promise<void> => {
    try {
      const { data } = await api.post<void>(REGISTER_COMPANY_URL, {
        company_name: params.companyName,
        company_address_1: params.companyAddress1,
        company_address_2: params.companyAddress2,
        company_city: params.companyCity,
        company_postcode: params.companyPostcode,
        company_website: params.companyWebsite,
        company_role: params.companyRole,
      });

      return data;
    } catch (error) {
      handleAuthError(error);
    }
  },

  setupCompanyBcIntegration: async (
    params: SetupCompanyBcIntegrationRequest
  ): Promise<void> => {
    try {
      const { data } = await api.post<void>(SETUP_COMPANY_BC_INTEGRATION_URL, {
        client_id: params.clientId,
        client_secret: params.clientSecret,
        customer_id: params.customerId,
      });

      return data;
    } catch (error) {
      handleAuthError(error);
    }
  },

  getCompanyRegistration: async (): Promise<
    CompanyRegistration | null | undefined
  > => {
    try {
      const { data } = await api.get<CompanyRegistration>(
        GET_COMPANY_REGISTRATION_URL
      );

      return data;
    } catch (error) {
      if (error instanceof AxiosError && error.response?.status === 404) {
        return null;
      }

      handleAuthError(error);
    }
  },

  sendForgotPassword: async (email: string): Promise<void> => {
    try {
      const { data } = await api.post<void>(FORGOT_PASSWORD_URL, {
        email,
      });

      return data;
    } catch (error) {
      handleAuthError(error);
    }
  },

  resetPassword: async (params: {
    token: string;
    password: string;
  }): Promise<void> => {
    try {
      const { data } = await api.post<void>(RESET_PASSWORD_URL, params);

      return data;
    } catch (error) {
      handleAuthError(error);
    }
  },
};
